<!-- resources/js/components/PantallaLlamadas.vue -->
<template>
    <div class="pantalla-llamadas">
        <!-- Escenario fijo 1920x1080 (16:9) que se escala para ocupar toda la pantalla -->
        <div class="escenario" :style="estiloEscenario">
            <!-- Fondo decorativo: destellos naranjas, puntos y edificio con bandera -->
            <svg class="fondo-decorativo" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
                <defs>
                    <linearGradient id="pl-destello-izq" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0" stop-color="#ffb02e"/>
                        <stop offset="0.55" stop-color="#ff7a00"/>
                        <stop offset="1" stop-color="#ff7a00" stop-opacity="0"/>
                    </linearGradient>
                    <linearGradient id="pl-destello-der" x1="1" y1="0" x2="0" y2="0">
                        <stop offset="0" stop-color="#ffb02e"/>
                        <stop offset="0.55" stop-color="#ff7a00"/>
                        <stop offset="1" stop-color="#ff7a00" stop-opacity="0"/>
                    </linearGradient>
                    <pattern id="pl-puntos" width="14" height="14" patternUnits="userSpaceOnUse">
                        <circle cx="3" cy="3" r="1.5" fill="#4a7fd0"/>
                    </pattern>
                    <linearGradient id="pl-fade-puntos" x1="1" y1="0" x2="0" y2="1">
                        <stop offset="0" stop-color="#fff"/>
                        <stop offset="1" stop-color="#000"/>
                    </linearGradient>
                    <mask id="pl-mask-puntos">
                        <rect x="1380" y="0" width="540" height="260" fill="url(#pl-fade-puntos)"/>
                    </mask>
                    <pattern id="pl-ventanas" width="28" height="36" patternUnits="userSpaceOnUse">
                        <rect x="4" y="6" width="20" height="24" fill="none" stroke="#2f6bd1" stroke-width="1.2"/>
                    </pattern>
                </defs>

                <!-- Puntos arriba a la derecha -->
                <rect x="1380" y="0" width="540" height="260" fill="url(#pl-puntos)" mask="url(#pl-mask-puntos)" opacity="0.55"/>

                <!-- Destellos esquina superior izquierda -->
                <path d="M0 0 H215 L0 78 Z" fill="url(#pl-destello-izq)"/>
                <path d="M0 92 L250 0 H274 L0 102 Z" fill="url(#pl-destello-izq)" opacity="0.7"/>

                <!-- Destellos esquina superior derecha -->
                <path d="M1920 0 H1790 L1920 48 Z" fill="url(#pl-destello-der)"/>
                <path d="M1920 60 L1752 0 H1730 L1920 70 Z" fill="url(#pl-destello-der)" opacity="0.6"/>

                <!-- Destello esquina inferior derecha -->
                <path d="M1388 1080 L1920 832 V884 L1500 1080 Z" fill="url(#pl-destello-der)" opacity="0.85"/>
                <path d="M1580 1080 L1920 922 V934 L1606 1080 Z" fill="url(#pl-destello-der)" opacity="0.6"/>

                <!-- Edificio institucional (línea) -->
                <g opacity="0.5" stroke="#2f6bd1" stroke-width="1.6" fill="none">
                    <polygon points="1466,1080 1466,932 1690,892 1690,1080" fill="url(#pl-ventanas)"/>
                    <polygon points="1690,892 1804,930 1804,1080 1690,1080" fill="url(#pl-ventanas)"/>
                    <line x1="1466" y1="932" x2="1578" y2="905"/>
                </g>
                <!-- Asta y bandera -->
                <line x1="1740" y1="836" x2="1740" y2="930" stroke="#9fb4d6" stroke-width="2" opacity="0.8"/>
                <g opacity="0.95">
                    <path d="M1742 838 L1798 846 V860 L1742 852 Z" fill="#d52b1e"/>
                    <path d="M1742 852 L1798 860 V874 L1742 866 Z" fill="#f9e300"/>
                    <path d="M1742 866 L1798 874 V888 L1742 880 Z" fill="#007934"/>
                </g>
            </svg>

            <!-- HEADER: Logo y título + Reloj -->
            <div class="header">
                <div class="logo-area">
                    <img src="/img/logo_minedu.png" alt="Logo DDE Beni" class="logo">
                    <div class="title-area">
                        <h1>SISTEMA DE GESTION DE COLAS</h1>
                        <p>LLAMADO A ATENCIÓN</p>
                    </div>
                </div>
                <div class="reloj-marco">
                    <div class="reloj-area">
                        <div class="fecha">{{ fechaActual }}</div>
                        <div class="hora">{{ horaActual }}</div>
                    </div>
                </div>
            </div>

            <!-- CONTENIDO PRINCIPAL -->
            <div class="contenido-principal">
                <!-- Loading -->
                <div v-if="loading" class="loading">
                    <div class="spinner"></div>
                    <p>Cargando...</p>
                </div>

                <div v-else class="layout-principal">
                    <!-- COLUMNA IZQUIERDA -->
                    <div class="columna-izquierda">
                        <!-- Panel principal: carrusel de videos (la llamada se resalta en la ventanilla, a la derecha) -->
                        <div class="panel-principal">
                            <!-- Anuncios: siempre montado, así el video se pausa durante la llamada
                                 y continúa desde el mismo punto al terminar -->
                            <div class="panel-contenido anuncios-contenido" :class="{ 'con-carrusel': hayVideos }">
                                <!-- Carrusel de videos (public/anuncios/anuncio1.mp4 ... anuncio7.mp4) -->
                                <div v-show="hayVideos" class="carrusel">
                                    <video
                                        ref="videoRef"
                                        class="slide-video"
                                        :src="videoActual"
                                        :muted="videoSilenciado"
                                        playsinline
                                        preload="auto"
                                        disablepictureinpicture
                                        @ended="siguienteVideo"
                                        @error="videoConError"
                                        @playing="videoReproduciendo"
                                        @timeupdate="marcarProgresoVideo"
                                    ></video>

                                    <div class="carrusel-indicadores">
                                        <span
                                            v-for="(src, i) in VIDEOS_ANUNCIOS"
                                            :key="'dot-' + src"
                                            class="punto"
                                            :class="{ activo: i === indiceVideo }"
                                        ></span>
                                    </div>
                                </div>

                                <!-- Sin videos disponibles: mensaje por defecto -->
                                <template v-if="!hayVideos">
                                    <svg class="icono-megafono" viewBox="0 0 120 100" fill="none" stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round">
                                        <g transform="rotate(-18 60 50)">
                                            <path d="M10 40 H30 L78 12 V76 L30 52 H10 Z"/>
                                            <path d="M30 52 L38 74 H52 L46 58"/>
                                            <path d="M92 24 L104 14"/>
                                            <path d="M96 44 H114"/>
                                            <path d="M92 64 L104 74"/>
                                        </g>
                                    </svg>
                                    <div class="anuncios-sub">ESPACIO DISPONIBLE PARA</div>
                                    <div class="anuncios-titulo">ANUNCIOS</div>
                                    <div class="anuncios-linea"></div>
                                </template>
                            </div>

                        </div>

                        <!-- Tickets en espera -->
                        <div class="cola-espera">
                            <div class="cola-header">
                                <h2>TICKETS EN ESPERA</h2>
                            </div>

                            <div class="cola-grid">
                                <div
                                    v-for="(ticket, i) in ticketsEnEspera.slice(0, 5)"
                                    :key="ticket.id ?? `${ticket.numero_ticket}-${i}`"
                                    class="cola-ticket"
                                >
                                    <svg class="cola-ticket-icono" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="M3 7 h18 v3 a2 2 0 0 0 0 4 v3 H3 v-3 a2 2 0 0 0 0 -4 z"/>
                                        <path d="M15 7 v10" stroke-dasharray="2 2"/>
                                    </svg>
                                    <div class="cola-ticket-datos">
                                        <div class="cola-ticket-numero">{{ ticket.numero_ticket }}</div>
                                        <div class="cola-ticket-ventanilla">
                                            Posición {{ ticket.posicion ?? i + 1 }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="ticketsEnEspera.length === 0" class="sin-espera">
                                No hay tickets en espera
                            </div>
                        </div>
                    </div>

                    <!-- COLUMNA DERECHA - Ventanilla actual -->
                    <div class="columna-derecha">
                        <div class="ventanilla-actual-card" :class="{ llamando: llamadaResaltada, sonando: hayLlamadaActiva }">
                            <div class="ventanilla-top">
                                <svg class="icono-escritorio" viewBox="0 0 64 56" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                                    <circle cx="32" cy="10" r="7"/>
                                    <path d="M20 30 C20 22 26 19 32 19 C38 19 44 22 44 30"/>
                                    <path d="M5 33 H59 L55 42 H9 Z"/>
                                    <path d="M12 42 V52"/>
                                    <path d="M52 42 V52"/>
                                </svg>
                                <div class="ventanilla-titulo">
                                    <div class="ventanilla-label">VENTANILLA</div>
                                    <div class="ventanilla-numero">
                                        {{ ventanillaMostrada }}
                                    </div>
                                </div>
                            </div>
                            <div class="atendiendo-bloque">
                                <div class="atendiendo-label">{{ llamadaResaltada ? 'LLAMANDO A' : 'ATENDIENDO' }}</div>
                                <div class="atendiendo-ticket">
                                    {{ ticketMostrado }}
                                </div>
                            </div>
                        </div>

                        <!-- Otras ventanillas -->
                        <div class="otras-ventanillas" v-if="otrasVentanillasVisibles.length > 0">
                            <div class="ventanillas-lista">
                                <div
                                    v-for="ventanilla in otrasVentanillasVisibles.slice(0, 6)"
                                    :key="ventanilla.id"
                                    class="ventanilla-item"
                                >
                                    <span class="item-codigo">{{ ventanilla.codigo?.replace('V', '') }}</span>
                                    <span class="item-nombre">VENTANILLA {{ ventanilla.codigo?.replace('V', '') }}</span>
                                    <span class="item-ticket" v-if="ventanilla.ticket_actual">
                                        {{ ventanilla.ticket_actual }}
                                    </span>
                                    <span v-else class="item-disponible">---</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import axios from 'axios'

// --- Kiosco: clave secreta (viene en ?k= desde el .bat) -------------------
// Se guarda en sessionStorage y se envía en el header de TODAS las peticiones (ver `api`).
const KIOSCO_STORAGE_KEY = 'kiosco_token'
let kioscoToken = new URLSearchParams(window.location.search).get('k')
try {
    if (kioscoToken) sessionStorage.setItem(KIOSCO_STORAGE_KEY, kioscoToken)
    else kioscoToken = sessionStorage.getItem(KIOSCO_STORAGE_KEY)
} catch (e) { /* sessionStorage no disponible */ }

// Cliente HTTP propio del kiosco: apunta a la API (/api/...) y envía la clave en cada
// petición. No toca el axios global del resto del sistema.
// Si su API no usa el prefijo /api, cambie API_BASE (p. ej. '' para rutas sin prefijo).
const API_BASE = '/api'
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
        ...(kioscoToken ? { 'X-Kiosco-Token': kioscoToken } : {})
    }
})

const loading = ref(true)
const ticketActual = ref(null)
const ticketAtendiendo = ref(null)
const ventanillaActual = ref(null)
const otrasVentanillas = ref([])
const ticketsEnEspera = ref([])
const fechaActual = ref('')
const horaActual = ref('')
const hayLlamadaActiva = ref(false)   // true mientras suena el ding-dong o la voz
const llamadaSonando = ref(false)     // true durante toda la cola de audio (silencia los videos)
const llamadaEnPantalla = ref(null)   // { ticket, ventanilla } que se muestra en el panel principal
const mostrarLlamada = ref(false)     // true: panel de llamada; false: carrusel de anuncios
let intervalId = null
let relojInterval = null
let ultimoTicketLlamado = null
let cargandoDatos = false      // evita llamadas simultáneas a la API
let desmontado = false
let ocultarLlamadaTimer = null
const MOSTRAR_LLAMADA_MS = 8000        // la llamada sigue resaltada unos segundos tras el audio

// La llamada se resalta en la tarjeta de ventanilla (derecha); los videos siguen.
const llamadaResaltada = computed(() => mostrarLlamada.value && !!llamadaEnPantalla.value)
const ventanillaMostrada = computed(() => llamadaResaltada.value
    ? ventanillaTexto(llamadaEnPantalla.value.ventanilla)
    : (ventanillaTexto(ventanillaActual.value?.codigo) || '05'))
const ticketMostrado = computed(() => llamadaResaltada.value
    ? llamadaEnPantalla.value.ticket
    : (ticketAtendiendo.value?.numero_ticket || '---'))
// Lista de la derecha: durante la llamada se quita la ventanilla llamada (ya está en la tarjeta)
// y se agrega la que normalmente ocupa la tarjeta, para que no desaparezca.
const otrasVentanillasVisibles = computed(() => {
    if (!llamadaResaltada.value) return otrasVentanillas.value
    const todas = [ventanillaActual.value, ...otrasVentanillas.value]
        .filter((v, i, arr) => v && arr.findIndex((o) => o?.id === v.id) === i)
    return todas.filter((v) => !mismaVentanilla(v.codigo, llamadaEnPantalla.value.ventanilla))
})

// ========== ESCALADO 16:9 (1920 x 1080) ==========
// El diseño se construye sobre un lienzo fijo de 1920x1080 y se escala para
// ocupar la pantalla completa manteniendo la proporción de la imagen.
const ANCHO_BASE = 1920
const ALTO_BASE = 1080
const escala = ref(1)
const offsetX = ref(0)
const offsetY = ref(0)

const ajustarEscala = () => {
    const w = window.innerWidth
    const h = window.innerHeight
    const s = Math.min(w / ANCHO_BASE, h / ALTO_BASE)
    escala.value = s
    offsetX.value = (w - ANCHO_BASE * s) / 2
    offsetY.value = (h - ALTO_BASE * s) / 2
}

const estiloEscenario = computed(() => ({
    transform: `translate(${offsetX.value}px, ${offsetY.value}px) scale(${escala.value})`
}))

// ========== CARRUSEL DE VIDEOS (ANUNCIOS) ==========
// Se reproducen en orden public/anuncios/anuncio1.mp4 ... anuncio7.mp4.
// Al terminar un video pasa al siguiente y, tras el último, vuelve al primero.
// - Se reproducen CON SONIDO. Para que Chrome lo permita sin que nadie toque la
//   pantalla, debe abrirse con --autoplay-policy=no-user-gesture-required (ver .bat).
//   Si Chrome aun así bloquea el audio, el video sigue en silencio y el sonido se
//   activa con el primer toque o clic en la pantalla.
// - No se superponen con las llamadas: durante el ding-dong y la voz el video sigue
//   en pantalla pero en silencio, y recupera el sonido al terminar la llamada.
// - Durante una llamada el video se pausa y luego continúa donde quedó.
// - Si un video falta o está dañado se salta; si fallan todos se muestra el
//   mensaje "ANUNCIOS" y se vuelve a intentar cada minuto.
const TOTAL_VIDEOS = 7
const VIDEOS_ANUNCIOS = Array.from({ length: TOTAL_VIDEOS }, (_, i) => `/anuncios/anuncio${i + 1}.mp4`)
const REINTENTO_VIDEOS_MS = 60000      // si fallan todos, reintentar cada minuto
const VIDEO_TRABADO_MS = 20000         // sin avance durante este tiempo → pasar al siguiente
const VIDEO_CON_SONIDO = true          // false = videos siempre silenciados
const VOLUMEN_VIDEO = 0.5              // 0.0 a 1.0 (volumen del video respecto al sistema)
const videoSilenciado = ref(!VIDEO_CON_SONIDO)
const videoRef = ref(null)
const indiceVideo = ref(0)
const hayVideos = ref(true)
const videoActual = computed(() => VIDEOS_ANUNCIOS[indiceVideo.value])
let fallosVideoSeguidos = 0
let ultimoProgresoVideo = Date.now()
let reintentoVideosTimer = null
let vigilanteVideoTimer = null

const reproducirVideo = () => {
    const video = videoRef.value
    if (!video || !hayVideos.value || desmontado) return
    video.volume = VOLUMEN_VIDEO
    video.muted = videoSilenciado.value || llamadaSonando.value
    const promesa = video.play()
    if (promesa?.catch) {
        promesa.catch((error) => {
            // AbortError es normal al cambiar de video mientras cargaba
            if (error?.name === 'AbortError') return
            if (error?.name === 'NotAllowedError' && !videoSilenciado.value) {
                // Chrome bloqueó el audio automático: seguir en silencio hasta el primer toque
                console.warn('Chrome bloqueó el audio del video; se activará al tocar la pantalla.')
                videoSilenciado.value = true
                video.muted = true
                video.play().catch(() => {})
                return
            }
            console.warn('No se pudo reproducir el video:', error)
        })
    }
}

// Activa el sonido del video tras un toque/clic (gesto que Chrome acepta para el audio)
const activarSonidoVideo = () => {
    if (!VIDEO_CON_SONIDO || !videoSilenciado.value) return
    videoSilenciado.value = false
    const video = videoRef.value
    if (video) {
        video.volume = VOLUMEN_VIDEO
        video.muted = llamadaSonando.value
    }
}

const irAVideo = (indice) => {
    indiceVideo.value = ((indice % TOTAL_VIDEOS) + TOTAL_VIDEOS) % TOTAL_VIDEOS
    ultimoProgresoVideo = Date.now()
    nextTick(() => {
        const video = videoRef.value
        if (!video) return
        video.load()          // necesario también cuando se repite el mismo archivo
        reproducirVideo()
    })
}

const siguienteVideo = () => irAVideo(indiceVideo.value + 1)

const videoReproduciendo = () => {
    fallosVideoSeguidos = 0
    ultimoProgresoVideo = Date.now()
}

const marcarProgresoVideo = () => {
    ultimoProgresoVideo = Date.now()
}

const videoConError = () => {
    console.warn(`No se pudo cargar ${videoActual.value}; se pasa al siguiente.`)
    fallosVideoSeguidos++
    if (fallosVideoSeguidos >= TOTAL_VIDEOS) {
        // Ningún video disponible: mensaje por defecto y nuevo intento más tarde
        hayVideos.value = false
        videoRef.value?.pause()
        clearTimeout(reintentoVideosTimer)
        reintentoVideosTimer = setTimeout(() => {
            fallosVideoSeguidos = 0
            hayVideos.value = true
            irAVideo(0)
        }, REINTENTO_VIDEOS_MS)
        return
    }
    siguienteVideo()
}

// Seguridad: si un video se queda congelado (archivo cortado, decodificador colgado)
// y nunca llega a "ended", se pasa al siguiente para que el carrusel no se detenga.
const iniciarVigilanteVideo = () => {
    clearInterval(vigilanteVideoTimer)
    vigilanteVideoTimer = setInterval(() => {
        if (!hayVideos.value || document.hidden) {
            ultimoProgresoVideo = Date.now()
            return
        }
        if (Date.now() - ultimoProgresoVideo > VIDEO_TRABADO_MS) {
            console.warn('Video sin avance; se pasa al siguiente.')
            siguienteVideo()
        }
    }, 5000)
}

// El <video> aparece cuando termina la carga inicial: arrancar el carrusel ahí
watch(videoRef, (video) => {
    if (video) irAVideo(indiceVideo.value)
})

// Durante el audio de la llamada el video sigue, pero sin sonido
watch(llamadaSonando, (sonando) => {
    const video = videoRef.value
    if (video) video.muted = sonando || videoSilenciado.value
})

// Estado del sistema de audio
const colaLlamadas = []        // llamadas pendientes de anunciar
let procesandoCola = false     // true mientras suena el ding-dong o la voz
let audioCtx = null            // contexto Web Audio para el ding-dong

// ========== FUNCIONES DE AUDIO ==========
// Cada llamada sigue este flujo: ding-dong → voz → pausa.
// Las llamadas se atienden de una en una mediante una cola, de modo que
// nunca se solapan ni se cortan entre sí. Todo funciona sin internet:
// el ding-dong se genera con Web Audio y la voz usa las voces instaladas
// en el equipo (Web Speech API).

const PAUSA_ANTES_MS = 400            // tiempo para que la pantalla se actualice
const PAUSA_ENTRE_LLAMADAS_MS = 1200  // silencio entre una llamada y la siguiente
const MAX_COLA = 5                    // máximo de llamadas pendientes
const TIMEOUT_VOZ_MS = 20000          // seguridad si el navegador nunca avisa el fin

const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Obtiene (o crea) el contexto de audio. Debe activarse tras un toque/clic,
 * o abrir Chrome con --autoplay-policy=no-user-gesture-required (modo kiosco).
 */
const obtenerAudioCtx = () => {
    try {
        if (!audioCtx) {
            const Ctx = window.AudioContext || window.webkitAudioContext
            if (!Ctx) return null
            audioCtx = new Ctx()
        }
        if (audioCtx.state === 'suspended') {
            audioCtx.resume().catch(() => {})
        }
        return audioCtx
    } catch (error) {
        console.warn('No se pudo iniciar el audio:', error)
        return null
    }
}

/**
 * Sonido corto "ding dong" (Mi5 → Do5), sintetizado localmente.
 * Dura ~1.3 s y devuelve una promesa que se resuelve al terminar.
 */
const reproducirDingDong = async () => {
    const ctx = obtenerAudioCtx()
    if (!ctx) return

    const inicio = ctx.currentTime + 0.05
    const notas = [
        { freq: 659.25, t: 0.0,  dur: 0.6 },   // ding
        { freq: 523.25, t: 0.35, dur: 0.85 }   // dong
    ]

    notas.forEach(({ freq, t, dur }) => {
        // Fundamental + armónico suave para un timbre de campana
        [{ mult: 1, pico: 0.5 }, { mult: 2, pico: 0.12 }].forEach(({ mult, pico }) => {
            const osc = ctx.createOscillator()
            const gain = ctx.createGain()
            osc.type = 'sine'
            osc.frequency.value = freq * mult
            gain.gain.setValueAtTime(0.0001, inicio + t)
            gain.gain.exponentialRampToValueAtTime(pico, inicio + t + 0.02)
            gain.gain.exponentialRampToValueAtTime(0.0001, inicio + t + dur)
            osc.connect(gain)
            gain.connect(ctx.destination)
            osc.start(inicio + t)
            osc.stop(inicio + t + dur + 0.05)
        })
    })

    await esperar(1300)
}

/**
 * Elige una voz en español, priorizando las locales (instaladas en el
 * equipo). Las voces "Google ..." de Chrome requieren internet.
 */
const elegirVoz = () => {
    const voces = window.speechSynthesis.getVoices()
    const espanolas = voces.filter((v) => v.lang?.toLowerCase().startsWith('es'))
    const local = espanolas.find((v) => v.localService)
    if (!local && espanolas.length > 0) {
        console.warn('No hay voz local en español; se usará una voz en línea.')
    }
    return local || espanolas[0] || null
}

/**
 * Lee un texto en voz alta y devuelve una promesa que se resuelve cuando
 * termina (o falla). No interrumpe ninguna otra voz: la cola lo garantiza.
 */
const hablar = async (mensaje) => {
    if (!('speechSynthesis' in window)) {
        console.warn('El navegador no soporta Speech Synthesis')
        return
    }
    const synth = window.speechSynthesis

    // Limpieza de restos de una voz anterior colgada (no debería ocurrir)
    if (synth.speaking || synth.pending) {
        synth.cancel()
        await esperar(150)
    }

    await new Promise((resolve) => {
        let terminado = false
        let seguro = null
        const terminar = () => {
            if (terminado) return
            terminado = true
            clearTimeout(seguro)
            resolve()
        }

        const utterance = new SpeechSynthesisUtterance(mensaje)
        utterance.lang = 'es-ES'
        utterance.rate = 0.9
        utterance.pitch = 1.0
        utterance.volume = 1.0

        const voz = elegirVoz()
        if (voz) {
            utterance.voice = voz
            utterance.lang = voz.lang
        }

        utterance.onstart = () => console.log(`🔊 Reproduciendo: "${mensaje}"`)
        utterance.onend = terminar
        utterance.onerror = (event) => {
            console.error('❌ Error en audio:', event.error)
            terminar()
        }

        // Algunos navegadores nunca disparan onend: evita bloquear la cola
        seguro = setTimeout(() => {
            synth.cancel()
            terminar()
        }, TIMEOUT_VOZ_MS)

        synth.speak(utterance)
    })
}

/**
 * Procesa la cola: una llamada a la vez (ding-dong → voz → pausa).
 */
const procesarCola = async () => {
    if (procesandoCola) return
    procesandoCola = true
    llamadaSonando.value = true

    try {
        while (!desmontado && colaLlamadas.length > 0) {
            const { ticket, ventanilla } = colaLlamadas.shift()
            const mensaje = `Atención. Ticket ${ticket}. Diríjase a la ventanilla ${ventanilla}.`

            clearTimeout(ocultarLlamadaTimer)
            llamadaEnPantalla.value = { ticket, ventanilla }
            mostrarLlamada.value = true
            hayLlamadaActiva.value = true
            await esperar(PAUSA_ANTES_MS)
            await reproducirDingDong()
            await hablar(mensaje)
            hayLlamadaActiva.value = false

            if (!desmontado && colaLlamadas.length > 0) {
                await esperar(PAUSA_ENTRE_LLAMADAS_MS)
            }
        }
    } finally {
        hayLlamadaActiva.value = false
        llamadaSonando.value = false
        procesandoCola = false
        // Tras el último anuncio, la tarjeta de ventanilla deja de resaltarse
        clearTimeout(ocultarLlamadaTimer)
        if (!desmontado) {
            ocultarLlamadaTimer = setTimeout(() => {
                mostrarLlamada.value = false
            }, MOSTRAR_LLAMADA_MS)
        }
    }
}

/**
 * Agrega una llamada a la cola (ignora duplicados ya pendientes).
 */
const encolarLlamada = (ticket, ventanilla) => {
    const key = `${ticket}_${ventanilla}`
    if (colaLlamadas.some((l) => l.key === key)) return
    if (colaLlamadas.length >= MAX_COLA) colaLlamadas.shift()
    colaLlamadas.push({ key, ticket, ventanilla })
    procesarCola()
}

/**
 * Repite la última llamada (útil si no se escuchó bien).
 * Si ya hay audio en curso no se interrumpe: el toque se ignora.
 */
const repetirUltimaLlamada = () => {
    if (!ultimoTicketLlamado || procesandoCola) return
    encolarLlamada(ultimoTicketLlamado.ticket, ultimoTicketLlamado.ventanilla)
}

// ========== FUNCIONES DE FECHA/HORA ==========

const formatearFecha = () => {
    const now = new Date()
    const opciones = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
    fechaActual.value = now.toLocaleDateString('es-ES', opciones)
        .replace(/^\w/, (c) => c.toUpperCase())
}

const formatearHora = () => {
    const now = new Date()
    horaActual.value = now.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    })
}

const iniciarReloj = () => {
    formatearFecha()
    formatearHora()
    relojInterval = setInterval(() => {
        formatearHora()
        formatearFecha() // se actualiza también al cambiar de día
    }, 1000)
}

// ========== FUNCIONES DE DATOS ==========

/**
 * Verifica si hay un nuevo ticket llamado y reproduce el audio
 */
const verificarNuevoTicket = (nuevoTicket, nuevaVentanilla) => {
    if (!nuevoTicket) return

    const ticketKey = `${nuevoTicket}_${nuevaVentanilla}`

    // Si el ticket es diferente al último reproducido, leerlo
    if (ticketKey !== ultimoTicketLlamado?.key) {
        ultimoTicketLlamado = {
            key: ticketKey,
            ticket: nuevoTicket,
            ventanilla: nuevaVentanilla
        }

        // La cola se encarga del orden, de la pausa previa y del ding-dong
        encolarLlamada(nuevoTicket, nuevaVentanilla)
    }
}

// ========== CARGA DE DATOS ==========

// Código de ventanilla normalizado: "V05", "05", 5 y {codigo: "V5"} → "5"
const codigoVentanilla = (valor) => {
    const crudo = valor && typeof valor === 'object' ? (valor.codigo ?? valor.numero ?? '') : valor
    const texto = String(crudo ?? '').replace(/^V/i, '').trim()
    return /^\d+$/.test(texto) ? String(Number(texto)) : texto
}
const mismaVentanilla = (a, b) => {
    const x = codigoVentanilla(a)
    return x !== '' && x === codigoVentanilla(b)
}
// Para mostrar: "5" → "05"
const ventanillaTexto = (valor) => {
    const c = codigoVentanilla(valor)
    return /^\d+$/.test(c) ? c.padStart(2, '0') : c
}

// Extrae la lista de una respuesta { success, data: [...] } (o data.tickets / data.data)
const listaDeRespuesta = (respuesta) => {
    const d = respuesta?.data?.data
    if (Array.isArray(d)) return d
    if (Array.isArray(d?.tickets)) return d.tickets
    if (Array.isArray(d?.data)) return d.data
    return null
}

let datosRecibidos = false   // los datos de demostración solo se usan si nunca hubo datos reales

const cargarDatos = async () => {
    if (cargandoDatos) return
    cargandoDatos = true
    try {
        // Cada consulta es independiente: si una falla, las otras se siguen mostrando
        const [llamadasR, turnosR, ventanillasR] = await Promise.allSettled([
            api.get('/pantalla/llamadas-actuales'),
            api.get('/pantalla/siguientes-turnos'),
            api.get('/pantalla/estado-ventanillas')
        ])

        if ([llamadasR, turnosR, ventanillasR].every((r) => r.status === 'rejected')) {
            console.error('Error al cargar datos:', llamadasR.reason)
            if (!datosRecibidos) cargarDatosDemo()
            return
        }
        datosRecibidos = true

        let nuevoTicket = null
        let nuevaVentanilla = null

        // ===== LLAMADAS ACTUALES =====
        if (llamadasR.status === 'fulfilled') {
            const lista = listaDeRespuesta(llamadasR.value)
            if (lista && lista.length > 0) {
                const llamada = lista[0]
                nuevoTicket = llamada.numero_ticket
                nuevaVentanilla = ventanillaTexto(llamada.ventanilla ?? llamada.ventanilla_codigo) || '05'
                ticketActual.value = llamada
            }
        } else {
            console.warn('No se pudo cargar /pantalla/llamadas-actuales:', llamadasR.reason)
        }

        // ===== TICKETS EN ESPERA =====
        if (turnosR.status === 'fulfilled') {
            const lista = listaDeRespuesta(turnosR.value)
            if (lista) {
                ticketsEnEspera.value = lista
            } else {
                console.warn('Formato inesperado en /pantalla/siguientes-turnos:', turnosR.value?.data)
            }
        } else {
            console.warn('No se pudo cargar /pantalla/siguientes-turnos:', turnosR.reason)
        }

        // ===== ESTADO DE VENTANILLAS =====
        if (ventanillasR.status === 'fulfilled') {
            const ventanillas = listaDeRespuesta(ventanillasR.value)

            if (ventanillas && ventanillas.length > 0) {
                // Ventanilla del ticket actual; si no, la primera ocupada o la primera
                let ventanillaEncontrada = null
                if (ticketActual.value?.ventanilla) {
                    ventanillaEncontrada = ventanillas.find((v) => mismaVentanilla(v.codigo, ticketActual.value.ventanilla))
                }
                if (!ventanillaEncontrada) {
                    ventanillaEncontrada = ventanillas.find((v) => v.estado === 'ocupada') || ventanillas[0]
                }

                ventanillaActual.value = ventanillaEncontrada
                ticketAtendiendo.value = ventanillaEncontrada.ticket_actual
                    ? { numero_ticket: ventanillaEncontrada.ticket_actual }
                    : null
                otrasVentanillas.value = ventanillas.filter((v) => v.id !== ventanillaEncontrada.id)

                if (ticketActual.value && !ticketActual.value.ventanilla) {
                    ticketActual.value.ventanilla = ventanillaEncontrada.codigo
                }
            } else if (ventanillas) {
                ventanillaActual.value = { id: 1, codigo: '05', nombre: 'Ventanilla 5', estado: 'disponible' }
            }
        } else {
            console.warn('No se pudo cargar /pantalla/estado-ventanillas:', ventanillasR.reason)
        }

        // ===== NUEVO TICKET LLAMADO → AUDIO + RESALTADO =====
        if (nuevoTicket && nuevaVentanilla) {
            verificarNuevoTicket(nuevoTicket, nuevaVentanilla)
        }
    } catch (error) {
        console.error('Error al procesar datos:', error)
    } finally {
        loading.value = false
        cargandoDatos = false
    }
}

// ========== DATOS DE DEMOSTRACIÓN ==========

const cargarDatosDemo = () => {
    const ticketDemo = 'A122'
    const ventanillaDemo = '05'

    ticketActual.value = {
        numero_ticket: ticketDemo,
        tipo_tramite: 'Trámites Administrativos',
        preferencia: 'Normal'
    }

    ticketAtendiendo.value = {
        numero_ticket: ticketDemo
    }

    ventanillaActual.value = {
        id: 5,
        codigo: '05',
        nombre: 'Ventanilla 5',
        estado: 'ocupada',
        operador: 'Juan Pérez',
        ticket_actual: ticketDemo
    }

    otrasVentanillas.value = [
        { id: 1, codigo: '01', nombre: 'Ventanilla 1', ticket_actual: 'A121' },
        { id: 2, codigo: '02', nombre: 'Ventanilla 2', ticket_actual: 'A120' },
        { id: 3, codigo: '03', nombre: 'Ventanilla 3', ticket_actual: 'A119' },
        { id: 4, codigo: '04', nombre: 'Ventanilla 4', ticket_actual: 'A118' },
        { id: 6, codigo: '06', nombre: 'Ventanilla 6', ticket_actual: 'A117' },
        { id: 7, codigo: '07', nombre: 'Ventanilla 7', ticket_actual: 'A116' },
        { id: 8, codigo: '08', nombre: 'Ventanilla 8', ticket_actual: null }
    ]

    ticketsEnEspera.value = [
        { numero_ticket: 'A123', posicion: 1 },
        { numero_ticket: 'A124', posicion: 2 },
        { numero_ticket: 'A125', posicion: 3 },
        { numero_ticket: 'A126', posicion: 4 },
        { numero_ticket: 'A127', posicion: 5 },
        { numero_ticket: 'A128', posicion: 6 },
        { numero_ticket: 'A129', posicion: 7 },
        { numero_ticket: 'A130', posicion: 8 }
    ]

    loading.value = false

    // Audio demo: se anuncia una sola vez (evita repetir en cada reintento)
    verificarNuevoTicket(ticketDemo, ventanillaDemo)
}

// ========== EVENTOS ==========

const handleTouchScreen = () => {
    obtenerAudioCtx() // el primer toque habilita el audio del navegador
    activarSonidoVideo()
    cargarDatos()
    // Al tocar la pantalla, repetir la última llamada si hay una
    if (ultimoTicketLlamado) {
        repetirUltimaLlamada()
    }
}

const iniciarAutoRefresh = () => {
    intervalId = setInterval(() => {
        cargarDatos()
    }, 5000)
}

// ========== CICLO DE VIDA ==========

onMounted(() => {
    // Cargar voces disponibles (necesario en algunos navegadores)
    if ('speechSynthesis' in window) {
        window.speechSynthesis.getVoices()
        window.speechSynthesis.onvoiceschanged = () => {
            window.speechSynthesis.getVoices()
        }
    }

    ajustarEscala()
    window.addEventListener('resize', ajustarEscala)

    cargarDatos()
    iniciarAutoRefresh()
    iniciarReloj()

    iniciarVigilanteVideo()

    window.addEventListener('touchstart', handleTouchScreen)
    window.addEventListener('click', handleTouchScreen)
})

onUnmounted(() => {
    if (intervalId) clearInterval(intervalId)
    if (relojInterval) clearInterval(relojInterval)
    desmontado = true
    clearInterval(vigilanteVideoTimer)
    clearTimeout(reintentoVideosTimer)
    if (videoRef.value) {
        videoRef.value.pause()
        videoRef.value.removeAttribute('src')
        videoRef.value.load()
    }
    clearTimeout(ocultarLlamadaTimer)
    colaLlamadas.length = 0
    if (window.speechSynthesis) {
        window.speechSynthesis.cancel()
    }
    if (audioCtx) {
        audioCtx.close().catch(() => {})
        audioCtx = null
    }
    window.removeEventListener('resize', ajustarEscala)
    window.removeEventListener('touchstart', handleTouchScreen)
    window.removeEventListener('click', handleTouchScreen)
})
</script>

<style scoped>
/* =====================================================================
   Pantalla de llamadas - televisor Full HD 1920 x 1080 px (16:9)
   El diseño vive en un lienzo fijo de 1920x1080 (.escenario) que se escala
   con transform para llenar cualquier pantalla 16:9 sin deformarse.
   Sólo usa fuentes del sistema (funciona sin internet).
   ===================================================================== */
.pantalla-llamadas {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    background: #02060d;
    overflow: hidden;
    cursor: none;
    user-select: none;
    -webkit-user-select: none;
}

.escenario {
    --naranja: #ff8c00;
    --naranja-claro: #ffb02e;
    --marco: rgba(255, 140, 0, 0.9);
    --brillo: 0 0 0 1px rgba(255, 140, 0, 0.12), 0 0 22px rgba(255, 140, 0, 0.28), inset 0 0 26px rgba(255, 140, 0, 0.07);

    position: absolute;
    top: 0;
    left: 0;
    width: 1920px;
    height: 1080px;
    transform-origin: top left;
    background:
        radial-gradient(ellipse 60% 55% at 32% 48%, #0b1f40 0%, transparent 70%),
        radial-gradient(ellipse 40% 40% at 90% 12%, #0a1a33 0%, transparent 70%),
        #050b17;
    color: white;
    font-family: 'Montserrat', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.fondo-decorativo {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    pointer-events: none;
}

.header,
.contenido-principal {
    position: relative;
    z-index: 1;
}

/* ========== HEADER ========== */
.header {
    flex: 0 0 242px;
    padding: 0 29px 0 61px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.logo-area {
    display: flex;
    align-items: center;
    gap: 48px;
}

.logo {
    height: 168px;
    width: auto;
    box-sizing: border-box;
    object-fit: contain;
    padding: 10px 22px;
    background: #ffffff;
    border-radius: 28px;
    border: 1px solid rgba(255, 255, 255, 0.6);
    box-shadow: 0 0 24px rgba(255, 255, 255, 0.14);
}

.title-area h1 {
    margin: 0;
    font-size: 48px;
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: 0.5px;
    color: var(--naranja);
    text-shadow: 0 0 18px rgba(255, 140, 0, 0.25);
}

.title-area p {
    margin: 8px 0 0;
    font-size: 24px;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: #ffffff;
}

/* ========== RELOJ (marco con esquinas cortadas) ========== */
.reloj-marco {
    flex-shrink: 0;
    width: 455px;
    height: 150px;
    filter: drop-shadow(0 0 12px rgba(255, 140, 0, 0.5));
}

.reloj-area {
    position: relative;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    background: linear-gradient(135deg, #ffb02e, #ff7a00 50%, #ffb02e);
    clip-path: polygon(30px 0, calc(100% - 30px) 0, 100% 30px, 100% calc(100% - 30px), calc(100% - 30px) 100%, 30px 100%, 0 calc(100% - 30px), 0 30px);
}

.reloj-area::before {
    content: '';
    position: absolute;
    inset: 3px;
    background: linear-gradient(135deg, #0b1b36, #061021);
    clip-path: polygon(28px 0, calc(100% - 28px) 0, 100% 28px, 100% calc(100% - 28px), calc(100% - 28px) 100%, 28px 100%, 0 calc(100% - 28px), 0 28px);
}

.fecha,
.hora {
    position: relative;
}

.fecha {
    font-size: 24px;
    font-weight: 700;
    color: #ffffff;
    letter-spacing: 0.3px;
    margin-bottom: 2px;
}

.hora {
    font-size: 60px;
    font-weight: 800;
    line-height: 1.05;
    font-variant-numeric: tabular-nums;
    color: var(--naranja);
    letter-spacing: 3px;
    text-shadow: 0 0 16px rgba(255, 140, 0, 0.45);
}

/* ========== CONTENIDO PRINCIPAL ========== */
.contenido-principal {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: 0 29px 48px;
}

.layout-principal {
    flex: 1;
    min-height: 0;
    display: flex;
    gap: 28px;
    width: 100%;
}

.columna-izquierda {
    flex: 74 1 0;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.columna-derecha {
    flex: 26 1 0;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

/* Marco común de todos los paneles */
.panel-principal,
.ventanilla-actual-card,
.otras-ventanillas,
.cola-espera {
    position: relative;
    box-sizing: border-box;
    border: 2px solid var(--marco);
    border-radius: 34px;
    background: linear-gradient(135deg, #0b1b36 0%, #071122 55%, #0a162b 100%);
    box-shadow: var(--brillo);
}

/* ========== PANEL PRINCIPAL (carrusel / llamada) ========== */
.panel-principal {
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    margin-bottom: 34px;
}

/* Franjas diagonales suaves */
.panel-principal::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(115deg,
        transparent 0 16%,
        rgba(70, 120, 200, 0.07) 16% 30%,
        transparent 30% 56%,
        rgba(70, 120, 200, 0.06) 56% 68%,
        transparent 68%);
}

/* Puntos en esquinas */
.panel-principal::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(circle, rgba(74, 127, 208, 0.6) 1.4px, transparent 1.9px) 0 0 / 16px 16px;
    -webkit-mask-image: radial-gradient(circle at 0% 22%, #000 0, transparent 34%), radial-gradient(circle at 100% 88%, #000 0, transparent 34%);
    mask-image: radial-gradient(circle at 0% 22%, #000 0, transparent 34%), radial-gradient(circle at 100% 88%, #000 0, transparent 34%);
}

.panel-contenido {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

/* Cambio entre anuncios y llamada */
.cambio-enter-active,
.cambio-leave-active {
    transition: opacity 0.35s ease, transform 0.35s ease;
}

.cambio-enter-from,
.cambio-leave-to {
    opacity: 0;
    transform: scale(0.97);
}

/* --- Carrusel de anuncios --- */
.anuncios-contenido.con-carrusel {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: block;
}

.carrusel {
    position: absolute;
    inset: 0;
    overflow: hidden;
    border-radius: 32px;
}

/* Video: se ajusta al panel sin deformarse (sobrante en negro azulado) */
.slide-video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #02060d;
    pointer-events: none;
}

.carrusel-indicadores {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 22px;
    z-index: 3;
    display: flex;
    justify-content: center;
    gap: 12px;
}

.punto {
    width: 12px;
    height: 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.4);
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.5);
    transition: width 0.4s ease, background 0.4s ease;
}

.punto.activo {
    width: 42px;
    background: var(--naranja);
    box-shadow: 0 0 12px rgba(255, 140, 0, 0.8);
}

/* --- Espacio de anuncios (sin videos) --- */
.icono-megafono {
    width: 168px;
    height: auto;
    color: var(--naranja);
    margin-bottom: 20px;
    filter: drop-shadow(0 0 12px rgba(255, 140, 0, 0.55));
}

.anuncios-sub {
    font-size: 32px;
    font-weight: 500;
    letter-spacing: 8px;
    color: rgba(255, 255, 255, 0.92);
}

.anuncios-titulo {
    font-size: 108px;
    font-weight: 900;
    line-height: 1.05;
    letter-spacing: 1px;
    background: linear-gradient(180deg, #ffc04d 0%, #ff8c00 55%, #ff6a00 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    filter: drop-shadow(0 0 16px rgba(255, 140, 0, 0.45));
}

.anuncios-linea {
    width: 640px;
    height: 3px;
    margin-top: 16px;
    background: radial-gradient(ellipse at center, #ffd27a 0, #ff8c00 30%, transparent 72%);
    box-shadow: 0 0 14px rgba(255, 140, 0, 0.6);
}

/* --- Llamada de ticket --- */
.ticket-label {
    font-size: 30px;
    font-weight: 500;
    letter-spacing: 8px;
    color: rgba(255, 255, 255, 0.85);
}

.ticket-numero-grande {
    font-size: 168px;
    font-weight: 900;
    line-height: 1.05;
    letter-spacing: 6px;
    margin: 10px 0 6px;
    background: linear-gradient(180deg, #ffc04d 0%, #ff8c00 55%, #ff6a00 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    filter: drop-shadow(0 0 18px rgba(255, 140, 0, 0.45));
}

.ticket-mensaje {
    font-size: 30px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.85);
}

.ventanilla-llamada {
    margin-top: 22px;
    padding: 12px 56px;
    background: rgba(255, 140, 0, 0.14);
    border-radius: 20px;
    border: 2px solid rgba(255, 140, 0, 0.55);
}

.ventanilla-llamada-numero {
    font-size: 50px;
    font-weight: 800;
    color: var(--naranja);
}

/* ========== TICKETS EN ESPERA ========== */
.cola-espera {
    flex-shrink: 0;
    padding: 18px 30px 26px;
}

.cola-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 22px;
    margin-bottom: 18px;
}

.cola-header::before,
.cola-header::after {
    content: '';
    flex: 0 0 130px;
    height: 2px;
}

.cola-header::before {
    background: linear-gradient(90deg, transparent, var(--naranja));
}

.cola-header::after {
    background: linear-gradient(90deg, var(--naranja), transparent);
}

.cola-header h2 {
    margin: 0;
    font-size: 38px;
    font-weight: 800;
    letter-spacing: 1px;
    color: var(--naranja);
}

.cola-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 14px;
}

.cola-ticket {
    min-width: 0;
    height: 112px;
    box-sizing: border-box;
    padding: 0 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: linear-gradient(135deg, rgba(20, 34, 60, 0.9), rgba(8, 15, 30, 0.95));
    border-radius: 18px;
    border: 1px solid rgba(255, 255, 255, 0.16);
}

.cola-ticket-icono {
    flex-shrink: 0;
    width: 46px;
    height: 46px;
    color: var(--naranja);
    transform: rotate(-45deg);
    filter: drop-shadow(0 0 6px rgba(255, 140, 0, 0.5));
}

.cola-ticket-datos {
    text-align: center;
    min-width: 0;
}

.cola-ticket-numero {
    font-size: 27px;
    font-weight: 800;
    white-space: nowrap;
    color: #ffffff;
    margin-bottom: 4px;
}

.cola-ticket-ventanilla {
    font-size: 20px;
    font-weight: 500;
    color: var(--naranja);
}

.sin-espera {
    text-align: center;
    padding: 24px;
    font-size: 26px;
    opacity: 0.5;
}

/* ========== COLUMNA DERECHA ========== */
.ventanilla-actual-card {
    flex-shrink: 0;
    padding: 18px 26px 22px;
    margin-bottom: 24px;
    text-align: center;
}

.ventanilla-top {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 22px;
}

.icono-escritorio {
    flex-shrink: 0;
    width: 92px;
    height: auto;
    color: var(--naranja);
    filter: drop-shadow(0 0 8px rgba(255, 140, 0, 0.5));
}

.ventanilla-titulo {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.ventanilla-label {
    font-size: 28px;
    font-weight: 700;
    letter-spacing: 1px;
    color: #ffffff;
}

.ventanilla-numero {
    font-size: 118px;
    font-weight: 900;
    line-height: 1;
    background: linear-gradient(180deg, #ffb02e 0%, #ff7a00 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    filter: drop-shadow(0 0 12px rgba(255, 140, 0, 0.5));
}

.atendiendo-bloque {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.atendiendo-label {
    font-size: 21px;
    font-weight: 700;
    letter-spacing: 1px;
    color: #ffffff;
    margin-bottom: 10px;
}

.atendiendo-ticket {
    padding: 10px 0;
    font-size: 46px;
    font-weight: 800;
    letter-spacing: 1px;
    color: #ffffff;
    background: rgba(4, 9, 20, 0.75);
    border: 1px solid rgba(255, 255, 255, 0.28);
    border-radius: 18px;
}

/* Llamada en curso: la tarjeta de ventanilla se resalta */
.ventanilla-actual-card {
    transition: transform 0.35s ease, border-color 0.35s ease, background 0.35s ease;
}

.ventanilla-actual-card.llamando {
    border-color: #ffb02e;
    background: linear-gradient(135deg, #3a2205 0%, #1d1406 55%, #0b1b36 100%);
    transform: scale(1.03);
    z-index: 2;
}

.ventanilla-actual-card.llamando.sonando {
    animation: llamadaPulse 1.5s ease-in-out infinite;
}

.ventanilla-actual-card.llamando .atendiendo-label {
    color: var(--naranja);
    letter-spacing: 4px;
}

.ventanilla-actual-card.llamando .atendiendo-ticket {
    font-size: 64px;
    font-weight: 900;
    color: #1a0f00;
    background: linear-gradient(180deg, #ffc04d 0%, #ff8c00 100%);
    border-color: #ffd27a;
    box-shadow: 0 0 28px rgba(255, 140, 0, 0.6);
}

@keyframes llamadaPulse {
    0%, 100% {
        box-shadow: 0 0 0 1px rgba(255, 140, 0, 0.3), 0 0 22px rgba(255, 140, 0, 0.35), inset 0 0 26px rgba(255, 140, 0, 0.1);
    }
    50% {
        box-shadow: 0 0 0 2px rgba(255, 176, 46, 0.7), 0 0 70px rgba(255, 140, 0, 0.75), inset 0 0 50px rgba(255, 140, 0, 0.2);
    }
}

/* Otras ventanillas */
.otras-ventanillas {
    flex: 0 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: 18px 20px;
    overflow: hidden;
}

.ventanillas-lista {
    flex: 0 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.ventanilla-item {
    flex: 0 1 76px;
    min-height: 48px;
    display: grid;
    grid-template-columns: 76px 1fr auto;
    column-gap: 10px;
    align-items: center;
    padding: 0 20px;
    background: linear-gradient(135deg, rgba(20, 34, 60, 0.9), rgba(8, 15, 30, 0.95));
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.14);
}

.item-codigo {
    font-size: 40px;
    font-weight: 800;
    color: var(--naranja);
}

.item-nombre {
    font-size: 20px;
    font-weight: 600;
    white-space: nowrap;
    color: rgba(255, 255, 255, 0.72);
}

.item-ticket {
    font-size: 28px;
    font-weight: 800;
    white-space: nowrap;
    color: #ffffff;
}

.item-disponible {
    font-size: 28px;
    font-weight: 600;
    letter-spacing: 2px;
    color: rgba(255, 255, 255, 0.7);
}

/* ========== LOADING ========== */
.loading {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.loading p {
    font-size: 28px;
}

.spinner {
    width: 80px;
    height: 80px;
    border: 5px solid rgba(255, 140, 0, 0.2);
    border-top: 5px solid #ff8c00;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 20px;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
</style>
